import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { LABELS } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { AccountPanel, ClientPanel } from "@/components/forms";
import { AttachBrokerPanel, CreateCycleAction, NewPropAccountPanel } from "@/components/prop-onboarding";
import { RevealPassword } from "@/components/pair";
import { ArchiveAccount, DeleteForever } from "@/components/delete";
import { DataTable, type DataTableColumn } from "@/components/table";
import { HoldButton, StateBadge } from "@/components/session";
import { Button, COMPUTER_ID_RE, ConnectionState, ErrorNote, Field, Hint, inputClass, useCanWrite, useIsAdmin } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date, dateTime, money } from "@/lib/format";
import { useAccountGroups, useClient, useCyclesAwaitingAccount, useRefreshDirectory, useSessionsAwaitingProp } from "@/lib/queries";
import type { Account, ClientDetail as ClientDetailData, CycleAwaitingAccount, ProxyCheck, SessionAwaitingProp } from "@/lib/types";

type LiveSession = NonNullable<Account["liveSession"]>;

export function ClientDetail() {
  const { id = "" } = useParams();
  const canWrite = useCanWrite();
  const isAdmin = useIsAdmin();
  const client = useClient(id);
  /*
   * LA FASE VIVA DI OGNI CONTO VIENE DA `GET /accounts`.
   *
   * `GET /clients/:id` porta i conti e i cicli, ma non dice QUALE conto sta in
   * quale Fase. `/accounts` quel campo ce l'ha per conto (`liveSession`), ed è
   * già in cache arrivando dalla pagina Conti.
   */
  const groups = useAccountGroups();
  const gruppo = groups.data?.find((g) => g.client.id === id) ?? null;
  const [editClient, setEditClient] = useState(false);
  /** La modifica di un conto: la creazione non passa più da qui, ma dal pulsante unico «Aggiungi conto». */
  const [accountPanel, setAccountPanel] = useState<{ open: boolean; account: Account | null }>({ open: false, account: null });
  /** Il pulsante unico da cui parte il percorso ciclo-fasi. Con un bersaglio, si apre già su quel ciclo o quella Fase in attesa. */
  const [newPropPanel, setNewPropPanel] = useState<{ open: boolean; cycleId?: string; sessionId?: string }>({ open: false });
  /** Le attese di QUESTO cliente: un ciclo o una Fase senza ciclo che non hanno ancora il loro conto prop. */
  const cicliInAttesa = useCyclesAwaitingAccount({ clientId: id });
  const fasiInAttesa = useSessionsAwaitingProp({ clientId: id });
  /** La Fase `AWAITING_BROKER` a cui si sta agganciando un broker, se c'è. */
  const [attachBrokerFor, setAttachBrokerFor] = useState<string | null>(null);
  /** Il ciclo sotto il cursore: evidenzia la sua scheda e quella della sua Fase viva, e viceversa. */
  const [hoverCycleId, setHoverCycleId] = useState<string | null>(null);

  const liveOf = (accountId: string): LiveSession | null => gruppo?.accounts.find((a) => a.id === accountId)?.liveSession ?? null;

  const cycleOfSession = useMemo(() => {
    const m = new Map<string, ClientDetailData["cycles"][number]>();
    for (const cy of client.data?.cycles ?? []) for (const s of cy.sessions) m.set(s.id, cy);
    return m;
  }, [client.data]);

  /*
   * Due conti con la stessa Fase viva sono la sua coppia: si ricostruisce dai
   * conti, non da una terza chiamata. Serve almeno il PROP: una Fase
   * `AWAITING_BROKER` c'è lo stesso, e senza mostrarla sparirebbe in silenzio
   * dalla scheda cliente — esattamente dove si va a chiuderla.
   */
  const faseVive = useMemo(() => {
    const map = new Map<string, { session: LiveSession; prop?: Account; broker?: Account }>();
    for (const a of client.data?.accounts ?? []) {
      const live = liveOf(a.id);
      if (!live || (live.state !== "ACTIVE" && live.state !== "PAUSED" && live.state !== "AWAITING_BROKER")) continue;
      const entry = map.get(live.id) ?? { session: live };
      if (a.role === "PROP") entry.prop = a;
      if (a.role === "BROKER") entry.broker = a;
      map.set(live.id, entry);
    }
    return [...map.values()].filter((f): f is { session: LiveSession; prop: Account; broker?: Account } => !!f.prop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.data, gruppo]);
  if (client.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (client.error || !client.data) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{client.error ? errorMessage(client.error) : "Cliente non trovato"}</ErrorNote>
        <Link to="/clienti" className="mt-3 inline-block text-sm text-muted hover:text-ink">
          Torna ai clienti
        </Link>
      </div>
    );
  }

  const c = client.data;
  const prop = c.accounts.filter((a) => a.role === "PROP");
  const broker = c.accounts.filter((a) => a.role === "BROKER");
  const senzaRuolo = c.accounts.filter((a) => a.role === "NONE");
  const propCurrency = prop[0]?.currency ?? "USD";

  const editAccount = (account: Account) => setAccountPanel({ open: true, account });
  // I cicli in attesa hanno la loro sezione: non si ripetono fra quelli attivi.
  const inAttesaIds = new Set((cicliInAttesa.data ?? []).map((cy) => cy.id));
  const cicliAttivi = c.cycles.filter((cy) => cy.state === "ACTIVE" && !inAttesaIds.has(cy.id));
  const cicliChiusi = c.cycles.filter((cy) => cy.state !== "ACTIVE");

  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={c.email}
        actions={
          <>
            <Link
              to={`/clienti/${c.id}/movimenti`}
              className="inline-flex items-center justify-center gap-1.5 rounded border border-line bg-raised px-3 py-1.5 text-sm font-medium text-ink hover:border-muted/60"
            >
              Registro movimenti
            </Link>
            {canWrite && (
              <>
                <Button onClick={() => setEditClient(true)}>Modifica cliente</Button>
                {/*
                  * UN SOLO PULSANTE per aggiungere un conto: la fase, la prop
                  * firm col suo programma e la contabilità si dichiarano nel
                  * pannello — mai una combinazione che il backend rifiuta.
                  */}
                <Button variant="primary" onClick={() => setNewPropPanel({ open: true })}>
                  Aggiungi conto
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="space-y-8 px-6 py-5">
        <IdentityBox client={c} canWrite={canWrite} isAdmin={isAdmin} />

        <section>
          <h2 className="mb-2 text-sm font-medium text-ink">Cicli attivi</h2>
          {cicliAttivi.length === 0 ? (
            <EmptyState title="Nessun ciclo attivo" hint="Un ciclo nasce con «Aggiungi conto» a contabilità accesa, o da «Crea ciclo» su una Fase 1 senza contabilità." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {cicliAttivi.map((cy) => {
                const viva = cy.sessions.find((x) => x.state === "ACTIVE" || x.state === "PAUSED" || x.state === "AWAITING_BROKER" || x.state === "AWAITING_PAYOUT");
                return (
                  <CycleCard
                    key={cy.id}
                    cycle={cy}
                    propCurrency={propCurrency}
                    highlighted={hoverCycleId === cy.id}
                    onHover={setHoverCycleId}
                    live={viva ?? null}
                    canWrite={canWrite}
                    onAttachBroker={setAttachBrokerFor}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Tutte le fasi, con ciclo e senza: la scheda dice a quale ciclo appartiene, o «Senza contabilità». Nella scheda del ciclo la fase resta, come rimando. */}
        <section>
          <h2 className="mb-2 text-sm font-medium text-ink">Fasi attive</h2>
          {faseVive.length === 0 ? (
            <EmptyState title="Nessuna fase attiva" hint="Una fase nasce con «Aggiungi conto» su un conto prop, con o senza contabilità." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {faseVive.map((f) => {
                const cy = cycleOfSession.get(f.session.id) ?? null;
                return (
                  <FaseVivaCard
                    key={f.session.id}
                    entry={f}
                    cycle={cy}
                    highlighted={!!cy && hoverCycleId === cy.id}
                    onHover={setHoverCycleId}
                    onAttachBroker={setAttachBrokerFor}
                  />
                );
              })}
            </div>
          )}
        </section>

        {((cicliInAttesa.data?.length ?? 0) > 0 || (fasiInAttesa.data?.length ?? 0) > 0) && (
          <section>
            <h2 className="mb-2 text-sm font-medium text-ink">Fasi in attesa del conto prop</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {(cicliInAttesa.data ?? []).map((cy) => (
                <AttesaCard
                  key={`ciclo-${cy.id}`}
                  title={cy.propFirm.name}
                  detail={`Ciclo ${cy.label ?? cy.propFirm.name} · ${LABELS.terms.recoverAmount} ${money(cy.recoverAmount, cy.currency)}`}
                  phase={LABELS.cyclePhase[cy.awaitingPropPhase]}
                  since={cy.awaitingPropSince}
                  canWrite={canWrite}
                  onRegister={() => setNewPropPanel({ open: true, cycleId: cy.id })}
                  to={`/cicli/${cy.id}`}
                />
              ))}
              {(fasiInAttesa.data ?? []).map((f: SessionAwaitingProp) => (
                <AttesaCard
                  key={`fase-${f.id}`}
                  title={f.preset ? `${f.preset.propFirm.name} · ${f.preset.name}` : "Fase senza programma"}
                  detail="Senza contabilità"
                  phase={LABELS.cyclePhase[f.awaitingPropPhase]}
                  since={f.awaitingPropSince}
                  canWrite={canWrite}
                  onRegister={() => setNewPropPanel({ open: true, sessionId: f.id })}
                  to={`/fasi/${f.id}`}
                />
              ))}
            </div>
          </section>
        )}

        {cicliChiusi.length > 0 && (
          <section>
            <h2 className="mb-2 text-sm font-medium text-ink">Cicli chiusi</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {cicliChiusi.map((cy) => (
                <CycleCard key={cy.id} cycle={cy} propCurrency={propCurrency} highlighted={hoverCycleId === cy.id} onHover={setHoverCycleId} live={null} canWrite={false} onAttachBroker={setAttachBrokerFor} />
              ))}
            </div>
          </section>
        )}

        <AccountsTable
          title="Conti prop"
          accounts={prop}
          withPropFirm
          canWrite={canWrite}
          onEdit={editAccount}
          liveOf={liveOf}
          noProxyWarning={prop.length > 0 && !c.defaultProxy}
          emptyHint="«Aggiungi conto» in alto crea il conto prop: è la gamba di partenza di ogni operazione."
        />
        <AccountsTable
          title="Conti broker"
          accounts={broker}
          canWrite={canWrite}
          onEdit={editAccount}
          liveOf={liveOf}
          emptyHint="«Aggiungi conto» → Conto broker: riceve la gamba opposta, dimensionata dal moltiplicatore."
        />
        {senzaRuolo.length > 0 && (
          <AccountsTable
            title="Conti senza ruolo"
            accounts={senzaRuolo}
            canWrite={canWrite}
            onEdit={editAccount}
            liveOf={liveOf}
            emptyHint=""
          />
        )}
      </div>

      <ClientPanel open={editClient} client={c} onClose={() => setEditClient(false)} />
      <AccountPanel
        open={accountPanel.open}
        clientId={c.id}
        clientDefaults={{ computerId: c.computerId, defaultProxy: c.defaultProxy }}
        account={accountPanel.account}
        defaultRole={accountPanel.account?.role ?? "PROP"}
        onClose={() => setAccountPanel((p) => ({ ...p, open: false, account: null }))}
      />
      {/* `key`: ogni apertura riparte da zero (e da un bersaglio nuovo), senza portarsi dietro la scelta di prima. */}
      <NewPropAccountPanel
        key={`${newPropPanel.cycleId ?? newPropPanel.sessionId ?? "nuovo"}-${newPropPanel.open}`}
        open={newPropPanel.open}
        clientId={c.id}
        clientName={c.name}
        brokerAccounts={broker}
        defaultTargetCycleId={newPropPanel.cycleId}
        defaultTargetSessionId={newPropPanel.sessionId}
        onClose={() => setNewPropPanel({ open: false })}
      />
      {attachBrokerFor && (
        <AttachBrokerPanel
          open
          sessionId={attachBrokerFor}
          clientId={c.id}
          clientName={c.name}
          brokerAccounts={broker}
          onClose={() => setAttachBrokerFor(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Cicli operativi
// ---------------------------------------------------------------------------

function Figure({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 font-medium text-ink">{value}</dd>
    </div>
  );
}

function CycleCard({
  cycle: cy,
  propCurrency,
  highlighted,
  onHover,
  live,
  canWrite,
  onAttachBroker,
}: {
  cycle: ClientDetailData["cycles"][number];
  propCurrency: string;
  highlighted: boolean;
  onHover: (id: string | null) => void;
  /** La Fase non conclusa del ciclo, se c'è: le sue azioni stanno qui, nella scheda del ciclo. */
  live: ClientDetailData["cycles"][number]["sessions"][number] | null;
  canWrite: boolean;
  onAttachBroker: (sessionId: string) => void;
}) {
  return (
    <div
      onMouseEnter={() => onHover(cy.id)}
      onMouseLeave={() => onHover(null)}
      className={`card space-y-3 p-4 transition-colors hover:border-muted/60 ${highlighted ? "!border-ink/40 !bg-raised" : ""}`}
    >
      <Link to={`/cicli/${cy.id}`} className="block space-y-3">
        <header className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">{cy.label ?? `${cy.propFirm.name} ${cy.preset.name}`}</p>
            <p className="mt-0.5 text-xs text-muted">
              {cy.propFirm.name} · {money(cy.accountSize, propCurrency)} · aperto {date(cy.openedAt)}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <StateBadge kind="cycle" state={cy.state} />
            <p className="mt-0.5 text-xs text-muted">{LABELS.cyclePhase[cy.currentPhase]}</p>
          </div>
        </header>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
          <Figure label="Da recuperare" value={money(cy.recoverAmount, propCurrency)} />
          <Figure label="Fee pagate" value={money(cy.feesPaid, propCurrency)} />
          <Figure label="Payout incassati" value={money(cy.payoutsReceived, propCurrency)} />
          {cy.closedPeriods.map((p) => (
            <Figure key={p.id} label={LABELS.cyclePhase[p.phase]} value={p.netCycle === null ? "—" : money(p.netCycle, propCurrency)} />
          ))}
        </dl>
      </Link>
      {live && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
          <span className="flex items-center gap-2 text-sm text-muted">
            {LABELS.cyclePhase[live.phase]}
            <StateBadge kind="session" state={live.state} />
          </span>
          <span className="flex items-center gap-2">
            {live.state === "AWAITING_BROKER" ? (
              canWrite && (
                <Button size="sm" onClick={() => onAttachBroker(live.id)}>
                  Aggiungi conto broker
                </Button>
              )
            ) : live.state === "ACTIVE" || live.state === "PAUSED" ? (
              <Link to={`/operazione?fase=${live.id}`}>
                <Button size="sm" variant="primary">
                  Apri operazione
                </Button>
              </Link>
            ) : null}
            <FaseMenu session={{ id: live.id, phase: live.phase, state: live.state }} hasCycle />
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Una attesa del conto prop, riquadrata in rosso: è tempo in cui quel cliente
 * non opera, e l'unica cosa da fare è registrare il conto.
 */
function AttesaCard({
  title,
  detail,
  phase,
  since,
  canWrite,
  onRegister,
  to,
}: {
  title: string;
  detail: string;
  phase: string;
  since: string;
  canWrite: boolean;
  onRegister: () => void;
  to: string;
}) {
  return (
    <div role="alert" className="space-y-2 rounded-card border border-loss/40 bg-loss/10 p-4">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={to} className="truncate font-medium text-ink hover:underline">
            {title}
          </Link>
          <p className="mt-0.5 text-xs text-muted">{detail}</p>
        </div>
        <span className="shrink-0 text-right text-xs text-muted">dal {date(since)}</span>
      </header>
      <p className="text-sm text-ink">Aspetta il conto prop: {phase}</p>
      {canWrite && (
        <Button size="sm" variant="primary" onClick={onRegister}>
          Registra il conto
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fasi vive
// ---------------------------------------------------------------------------

function FaseLeg({ side, account }: { side: "PROP" | "BROKER"; account: Account }) {
  const isProp = side === "PROP";
  return (
    <section className={`${isProp ? "pair-side-prop" : "pair-side-broker"} min-w-0 p-3`}>
      <p className={`text-xs font-medium ${isProp ? "text-prop" : "text-broker"}`}>{isProp ? "Prop" : "Broker"}</p>
      <p className="truncate text-sm font-medium text-ink">{account.label}</p>
      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted">
        <span className="machine-id">{account.mt5Login}</span>
        <span className="truncate">{account.server.displayName ?? account.server.name}</span>
      </p>
      <p className="mt-1 flex items-center justify-between gap-2 text-sm">
        <span className="text-muted">{account.lastEquity === null ? "—" : money(account.lastEquity, account.currency)}</span>
        <ConnectionState connected={account.isConnected} error={account.lastConnectError} compact />
      </p>
      <div className="mt-2">
        <AccountQuickActions account={account} size="sm" />
      </div>
    </section>
  );
}

function FaseVivaCard({
  entry,
  cycle,
  highlighted,
  onHover,
  onAttachBroker,
}: {
  entry: { session: LiveSession; prop: Account; broker?: Account };
  /** Il ciclo a cui la Fase appartiene; null = senza contabilità. */
  cycle: ClientDetailData["cycles"][number] | null;
  highlighted: boolean;
  onHover: (id: string | null) => void;
  onAttachBroker: (sessionId: string) => void;
}) {
  const { session, prop, broker } = entry;
  const cycleId = cycle?.id ?? null;
  return (
    <div
      onMouseEnter={() => cycleId && onHover(cycleId)}
      onMouseLeave={() => onHover(null)}
      className={`card space-y-3 p-4 transition-colors ${highlighted ? "!border-ink/40 !bg-raised" : ""}`}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-ink">{LABELS.cyclePhase[session.phase]}</p>
          <p className="mt-0.5 truncate text-xs text-muted">
            {cycle ? (
              <Link to={`/cicli/${cycle.id}`} className="hover:text-ink hover:underline">
                Ciclo {cycle.label ?? session.cycleLabel ?? `${cycle.propFirm.name} ${cycle.preset.name}`}
              </Link>
            ) : (
              "Senza contabilità"
            )}
          </p>
        </div>
        <StateBadge kind="session" state={session.state} />
      </header>

      <div className="pair">
        <FaseLeg side="PROP" account={prop} />
        {broker ? (
          <FaseLeg side="BROKER" account={broker} />
        ) : (
          // Fase in attesa del broker: nessuna gamba da mostrare, e il gesto per uscirne è qui.
          <section className="pair-side-broker flex min-w-0 flex-col items-start justify-center gap-2 p-3">
            <p className="text-xs font-medium text-broker">Broker</p>
            <p className="text-xs text-muted">Nessuna apertura finché non arriva.</p>
            <Button size="sm" onClick={() => onAttachBroker(session.id)}>
              Aggiungi conto broker
            </Button>
          </section>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {broker ? (
          <Link to={`/operazione?fase=${session.id}`}>
            <Button size="sm" variant="primary">
              Apri operazione
            </Button>
          </Link>
        ) : (
          <span />
        )}
        <FaseMenu session={session} hasCycle={!!cycleId} />
      </div>
    </div>
  );
}

function MenuLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} role="menuitem" className="block rounded px-2 py-1.5 text-sm text-ink hover:bg-raised">
      {children}
    </Link>
  );
}

/**
 * «Modifica parametri», «Fase superata» e «Conto bruciato» restano dove sono,
 * su `/fasi/:id`: i loro form sono già lì (`ParamsTab`, l'esito) e non sono
 * componenti condivisi. Qui solo le voci che ci portano. Pausa e Riprendi
 * invece sono azioni dirette — `HoldButton`, già condiviso.
 */
function FaseMenu({ session, hasCycle }: { session: Pick<LiveSession, "id" | "phase" | "state">; hasCycle: boolean }) {
  return (
    <MoreMenu label="Altre azioni sulla fase">
      <MenuLink to={`/fasi/${session.id}`}>Apri la fase</MenuLink>
      {/* Solo su una Fase senza ciclo; su Fase 2 e Funded la voce c'è ma è disabilitata, col motivo. */}
      {!hasCycle && session.state !== "AWAITING_PAYOUT" && <CreateCycleAction session={{ id: session.id, phase: session.phase, cycle: null }} variant="menu" />}
      <MenuLink to={`/fasi/${session.id}`}>Modifica parametri</MenuLink>
      <div className="px-1 py-0.5">
        <HoldButton session={{ id: session.id, state: session.state }} size="sm" />
      </div>
      <MenuLink to={`/fasi/${session.id}`}>Fase superata</MenuLink>
      <MenuLink to={`/fasi/${session.id}`}>Conto bruciato</MenuLink>
    </MoreMenu>
  );
}

// ---------------------------------------------------------------------------
// «…»: il menu che non c'era
// ---------------------------------------------------------------------------

/**
 * Si chiude al clic fuori o con Esc — MAI a un clic dentro: dentro ci vivono
 * `RevealPassword`, `ArchiveAccount`, `DeleteForever`, che sono flussi a più
 * passi (mostra, conferma, un secondo clic). Chiudersi al primo clic
 * interromperebbe esattamente quel passo.
 */
function MoreMenu({ children, label = "Altre azioni" }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <Button size="sm" variant="ghost" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((o) => !o)}>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-20 mt-1 w-56 space-y-1 rounded border border-line bg-surface p-1.5 shadow-lg">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Le azioni di un conto: connetti, rileggi il saldo, modifica
// ---------------------------------------------------------------------------

/**
 * Connetti/Disconnetti e Aggiorna saldo, e Modifica quando c'è un posto dove
 * farlo aprire. **Non `Mostra password`**: quella entra in audit a ogni
 * lettura, e nella tabella dei conti sta nel menu «…» insieme ad Archivia ed
 * Elimina — qui, sulla scheda Fase viva, non c'è affatto.
 */
function AccountQuickActions({ account, onEdit, size }: { account: Account; onEdit?: (a: Account) => void; size?: "sm" }) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (name: string, fn: () => Promise<unknown>) => async () => {
    setBusy(name);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {account.isConnected ? (
        <Button size={size} disabled={!!busy} onClick={run("disconnect", () => api.accounts.disconnect(account.id))}>
          {busy === "disconnect" ? "Disconnessione…" : "Disconnetti"}
        </Button>
      ) : (
        <Button size={size} disabled={!!busy || account.role === "NONE"} onClick={run("connect", () => api.accounts.connect(account.id))}>
          {busy === "connect" ? "Connessione…" : "Connetti"}
        </Button>
      )}
      <Button size={size} variant="ghost" disabled={!!busy || !account.mirrorTraderName} onClick={run("sync", () => api.accounts.sync(account.id))}>
        {busy === "sync" ? "Lettura…" : "Aggiorna saldo"}
      </Button>
      {onEdit && (
        <Button size={size} variant="ghost" disabled={!!busy} onClick={() => onEdit(account)}>
          Modifica
        </Button>
      )}
      {error && <span className="text-xs text-loss">{error}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Conti prop / Conti broker / Conti senza ruolo
// ---------------------------------------------------------------------------

function AccountsTable({
  title,
  accounts,
  withPropFirm = false,
  canWrite,
  onEdit,
  liveOf,
  noProxyWarning = false,
  emptyHint,
}: {
  title: string;
  accounts: Account[];
  withPropFirm?: boolean;
  canWrite: boolean;
  onEdit: (a: Account) => void;
  liveOf: (id: string) => LiveSession | null;
  noProxyWarning?: boolean;
  emptyHint: string;
}) {
  const columns: DataTableColumn<Account>[] = [
    { key: "label", header: "Nome", cell: (a) => <span className="font-medium text-ink">{a.label}</span>, sortBy: (a) => a.label.toLowerCase() },
  ];
  if (withPropFirm) {
    columns.push({
      key: "propFirm",
      header: "Prop firm",
      cell: (a) => a.server.propFirm?.name ?? "—",
      sortBy: (a) => a.server.propFirm?.name ?? "",
    });
  }
  columns.push(
    { key: "login", header: "Conto", cell: (a) => a.mt5Login, className: "machine-id", sortBy: (a) => a.mt5Login },
    {
      key: "server",
      header: "Server",
      cell: (a) => a.server.displayName ?? a.server.name,
      sortBy: (a) => a.server.displayName ?? a.server.name,
    },
    {
      key: "equity",
      header: "Equity",
      align: "right",
      cell: (a) => (a.lastEquity === null ? "—" : money(a.lastEquity, a.currency)),
      sortBy: (a) => (a.lastEquity === null ? null : Number(a.lastEquity)),
    },
    {
      key: "phase",
      header: "Fase",
      cell: (a) => {
        const live = liveOf(a.id);
        return live ? (
          <Link to={`/fasi/${live.id}`} className="text-ink hover:underline">
            {LABELS.cyclePhase[live.phase]}
          </Link>
        ) : (
          <span className="text-faint">—</span>
        );
      },
    },
    {
      key: "cycle",
      header: "Ciclo",
      cell: (a) => liveOf(a.id)?.cycleLabel ?? <span className="text-faint">—</span>,
    },
    {
      key: "connection",
      header: "Connessione",
      cell: (a) => <ConnectionState connected={a.isConnected} error={a.lastConnectError} compact />,
      sortBy: (a) => (a.isConnected ? 1 : 0),
    },
  );
  if (canWrite) {
    columns.push({
      key: "actions",
      header: "",
      align: "right",
      cell: (a) => (
        <span className="flex items-center justify-end gap-2">
          <AccountQuickActions account={a} onEdit={onEdit} size="sm" />
          <MoreMenu label={`Altre azioni su ${a.label}`}>
            <div className="px-1 py-0.5">
              <RevealPassword account={a} />
            </div>
            <div className="px-1 py-0.5">
              <ArchiveAccount id={a.id} label={a.label} size="sm" />
            </div>
            <div className="px-1 py-0.5">
              <DeleteForever kind="conto" id={a.id} label={a.label} size="sm" />
            </div>
          </MoreMenu>
        </span>
      ),
    });
  }

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-ink">{title}</h2>
      </div>
      {noProxyWarning && (
        <p className="mb-2 text-xs text-loss">Nessun proxy sul cliente: i conti prop non si connettono finché non gliene assegni uno.</p>
      )}
      <div className="card">
        <DataTable
          columns={columns}
          rows={accounts}
          rowKey={(a) => a.id}
          empty={{ title: "Nessun conto", hint: emptyHint }}
          initialSort={{ key: "label", dir: "asc" }}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CID, proxy e accesso: un riquadro solo
// ---------------------------------------------------------------------------

/**
 * Il CID, il proxy e l'accesso del cliente, in un riquadro solo: sono i tre
 * dati con cui il cliente si presenta al mondo (dispositivo, uscita di rete,
 * portale). Le spiegazioni di ieri erano paragrafi fissi; qui sono un `Hint`,
 * tranne l'avviso che blocca un'azione — «nessun proxy» — che resta in
 * chiaro.
 */
function IdentityBox({ client: c, canWrite, isAdmin }: { client: ClientDetailData; canWrite: boolean; isAdmin: boolean }) {
  return (
    <section className="card divide-y divide-line">
      <div className="grid gap-6 p-4 sm:grid-cols-2 lg:grid-cols-3">
        <ComputerIdCell client={c} isAdmin={isAdmin} />
        <ProxyCell client={c} canWrite={canWrite} />
        {c.notes && (
          <div className="min-w-0">
            <p className="text-xs text-faint">Note</p>
            <p className="mt-0.5 text-sm text-ink">{c.notes}</p>
          </div>
        )}
      </div>
      {isAdmin && <AccessSection client={c} />}
    </section>
  );
}

function ComputerIdCell({ client: c, isAdmin }: { client: ClientDetailData; isAdmin: boolean }) {
  const refresh = useRefreshDirectory();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"GENERATE" | "SET">("GENERATE");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scritto = value.trim();
  const invalido = mode === "SET" && scritto !== "" && !COMPUTER_ID_RE.test(scritto);

  const chiudi = () => {
    setOpen(false);
    setMode("GENERATE");
    setValue("");
    setError(null);
  };

  const conferma = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.clients.setComputerId(c.id, mode === "GENERATE" ? { mode: "GENERATE" } : { mode: "SET", value: scritto });
      await refresh();
      chiudi();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-w-0">
      <p className="flex items-center text-xs text-faint">
        CID
        <Hint>Generato alla creazione del cliente: è il dispositivo con cui i suoi conti si presentano alla prop firm.</Hint>
      </p>
      <p className="mt-0.5 flex items-center gap-2">
        <span className="machine-id">{c.computerId}</span>
        {isAdmin && (
          <button
            type="button"
            onClick={() => (open ? chiudi() : setOpen(true))}
            aria-expanded={open}
            aria-label="Cambia il CID"
            title="Cambia il CID"
            className="text-xs text-muted hover:text-ink"
          >
            ✏
          </button>
        )}
      </p>

      {open && (
        <div className="card mt-2 w-80 max-w-full space-y-2 p-3 text-xs">
          <p className="text-muted">
            CID attuale <span className="machine-id text-ink">{c.computerId}</span>
          </p>

          <div role="radiogroup" aria-label="Come cambiare il CID" className="inline-flex overflow-hidden rounded border border-line">
            {(["GENERATE", "SET"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                onClick={() => setMode(m)}
                className={`px-3 py-1 ${mode === m ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
              >
                {m === "GENERATE" ? "Genera nuovo" : "Scrivi"}
              </button>
            ))}
          </div>

          {mode === "SET" && (
            <>
              <input
                aria-label="CID nuovo"
                value={value}
                maxLength={32}
                placeholder="32 caratteri esadecimali"
                onChange={(e) => setValue(e.target.value.toUpperCase())}
                className={`${inputClass} machine-id py-1`}
              />
              {invalido && <p className="text-loss">32 caratteri esadecimali, come quello generato.</p>}
            </>
          )}

          <p className="text-muted">
            Tutti i conti di questo cliente si presenteranno alla prop firm con un dispositivo nuovo dalla prossima connessione.
            Una firm può notare il cambio.
          </p>

          {error && <p className="text-loss">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={chiudi}>
              Annulla
            </Button>
            <Button size="sm" variant="primary" disabled={busy || (mode === "SET" && !COMPUTER_ID_RE.test(scritto))} onClick={() => void conferma()}>
              {busy ? "Cambio…" : "Conferma"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProxyCell({ client: c, canWrite }: { client: ClientDetailData; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState<ProxyCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = c.defaultProxy;

  const controlla = async () => {
    if (!p) return;
    setBusy(true);
    setError(null);
    setCheck(null);
    try {
      setCheck((await api.proxies.check(p.id)).check);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!p) {
    return (
      <div className="min-w-0">
        <p className="text-xs text-faint">Proxy</p>
        {/* Blocca un rischio nuovo (i conti prop non si connettono): resta in chiaro, non in un Hint. */}
        <p className="mt-0.5 text-warn">Nessuno: i conti prop non si connettono</p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <p className="text-xs text-faint">Proxy</p>
      <p className="mt-0.5 text-ink">
        {p.label ? `${p.label} · ` : ""}
        {p.host}:{p.port}
        {[p.city, p.country].filter(Boolean).length ? ` · ${[p.city, p.country].filter(Boolean).join(", ")}` : ""}
      </p>
      <p className="text-xs text-faint">
        {p.type}
        {p.username ? ` · ${p.username}` : ""}
        {p.sessionId ? (
          <>
            {" · sessione "}
            <span className="machine-id">{p.sessionId}</span>
          </>
        ) : null}
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        {p.lastCheckAt ? (
          <span className={p.lastCheckOk ? "text-gain" : "text-loss"}>
            {dateTime(p.lastCheckAt)} · {p.lastCheckOk ? "raggiungibile" : "non raggiungibile"}
          </span>
        ) : (
          <span className="text-muted">Mai controllato</span>
        )}
        {canWrite && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void controlla()}>
            {busy ? "Controllo…" : "Controlla"}
          </Button>
        )}
      </p>
      {check && (
        <p className={`text-xs ${check.ok ? "text-gain" : "text-loss"}`}>
          {check.detail} · {check.elapsedMs} ms
        </p>
      )}
      {error && <p className="text-xs text-loss">{error}</p>}
    </div>
  );
}

/**
 * L'accesso del cliente: darlo, spegnerlo, riaccenderlo, rifargli la
 * password. Solo ADMIN, perché le rotte sono sue — la stessa regola della
 * matita del CID qui sopra.
 */
function AccessSection({ client: c }: { client: ClientDetailData }) {
  const refresh = useRefreshDirectory();
  const [form, setForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mostra, setMostra] = useState<{ email: string; password: string } | null>(null);

  const a = c.access;
  const senzaEmail = c.email.trim() === "";

  const azione = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const crea = () =>
    azione(async () => {
      const creato = await api.clients.createAccess(c.id);
      setMostra({ email: creato.email, password: creato.password });
      setForm(false);
    });

  const reimposta = () =>
    azione(async () => {
      const nuova = await api.users.resetPassword(a!.id);
      setMostra({ email: nuova.email, password: nuova.password });
    });

  return (
    <div className="space-y-3 p-4 text-sm">
      <p className="flex items-center text-xs text-faint">
        Accesso del cliente
        <Hint>L'accesso con cui entra nell'area riservata. La password si vede solo una volta: si genera, si mostra e non si rilegge.</Hint>
      </p>
      {a ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="machine-id">{a.email}</span>
          <span className={a.isActive ? "text-gain" : "text-loss"}>{a.isActive ? "Attivo" : "Disattivato"}</span>
          {a.mustChangePassword && <span className="text-muted">Deve cambiare la password al primo accesso</span>}
          <span className="ml-auto inline-flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                void azione(async () => {
                  await api.users.setActive(a.id, !a.isActive);
                })
              }
            >
              {a.isActive ? "Disattiva" : "Riattiva"}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void reimposta()}>
              Reimposta password
            </Button>
          </span>
        </div>
      ) : form ? (
        <div className="space-y-2">
          <Field label="Email d'accesso">{() => <span className="machine-id">{c.email}</span>}</Field>
          <p className="text-xs text-faint">
            La password la genera il gestionale e <strong>si vede una volta sola</strong>: al primo accesso il cliente dovrà cambiarla.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setForm(false);
                setError(null);
              }}
            >
              Annulla
            </Button>
            <Button size="sm" variant="primary" disabled={busy} onClick={() => void crea()}>
              {busy ? "Creo…" : "Crea accesso"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-muted">
            {senzaEmail ? "Serve l'email del cliente per creare un accesso." : "Nessun accesso: questo cliente non entra nell'area riservata."}
          </span>
          <Button size="sm" className="ml-auto" disabled={senzaEmail} onClick={() => setForm(true)}>
            Crea accesso
          </Button>
        </div>
      )}

      {mostra && (
        <div role="status" className="space-y-2 rounded border border-line bg-surface px-3 py-2">
          <p className="text-ink">
            <strong>Si vede solo adesso</strong>: nel database c'è soltanto l'hash, e una password non si rilegge — si sostituisce.
          </p>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted">Email</dt>
              <dd className="font-mono text-ink">{mostra.email}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted">Password</dt>
              <dd className="select-all font-mono text-ink">{mostra.password}</dd>
            </div>
          </dl>
          <div className="flex justify-end">
            <Button size="sm" variant="primary" onClick={() => setMostra(null)}>
              Ho copiato la password
            </Button>
          </div>
        </div>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}
